import { Option } from '@fluentui/react-components';
import {
  ArrowRightRegular,
  CheckmarkRegular,
  DismissRegular,
  PeopleFilled,
} from '@fluentui/react-icons';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AzureGroupInfoResponse } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { uniqBy } from 'lodash';
import { Button } from '../../Button';
import { Combobox } from '../../Combobox';
import { Flex } from '../../Flex';
import { Text } from '../../Text';
import { useInputAppearance } from '../../hooks';
import { ErrorType, MappingState } from '../CourseMapping.enums';
import { CourseMappingAssignmentComponentProps } from '../CourseMapping.interfaces';
import { buildAzureGroupOptionLabels, getIconFromMappingState } from '../CourseMapping.utils';
import './CourseSetComponent.scss';

export const CourseSetComponent: FC<CourseMappingAssignmentComponentProps> = (props) => {
  const { t } = useTranslation();
  const inputAppearance = useInputAppearance();
  const { assignment, cloudCourseSelection, error, update, azureGroupIdsInUseElsewhere } = props;
  const timetableCourse = assignment.timetableCourse;
  const mappingType = assignment.mappingType;
  const hasError: boolean = !!error && error > ErrorType.none;

  const courseOptions = useMemo(
    () => uniqBy(cloudCourseSelection, (group) => group.id),
    [cloudCourseSelection],
  );

  // Labels disambiguate groups that share a display name by appending the mail
  // address in round brackets. Display only — never stored as azureGroupName.
  const optionLabels = useMemo(
    () => buildAzureGroupOptionLabels(courseOptions),
    [courseOptions],
  );
  const labelFor = (group: AzureGroupInfoResponse) =>
    optionLabels.get(group.id) ?? group.displayName ?? '';

  // Marked, not hidden: a group has to stay pickable so it can be moved from one
  // entity to another within a single save.
  const optionLabelFor = (group: AzureGroupInfoResponse) =>
    azureGroupIdsInUseElsewhere?.has(group.id)
      ? `${labelFor(group)} — ${t('settings:courseimport:group_already_assigned_marker', {
          defaultValue: 'bereits zugewiesen',
        })}`
      : labelFor(group);

  // A server suggestion is only pre-selected in the combobox, never applied: the
  // row stays AutoMapped with azureGroupId === null until the user confirms it
  // via the checkmark (-> ManuallyMapped) or dismisses it via the reset (×).
  const appliedId = assignment.azureGroupId;
  const appliedName = assignment.azureGroupName ?? '';
  const suggestedName = assignment.suggestedAzureGroupName ?? '';
  const isSuggestion = assignment.mappingState === MappingState.AutoMapped;
  const selectedId = appliedId ?? assignment.suggestedAzureGroupId ?? null;
  // Show the disambiguated label for the selected group when it is in the pool,
  // so a duplicate name stays distinguishable; otherwise fall back to the
  // stored plain name (e.g. a group no longer present / cloud-course error).
  const selectedLabel = selectedId ? optionLabels.get(selectedId) : undefined;
  const displayName = selectedLabel ?? (appliedName || suggestedName);

  const [inputValue, setInputValue] = useState(displayName);

  // Keep the visible text in sync when the selection changes from the outside
  // (e.g. reset or an updated mapping coming in via props).
  useEffect(() => {
    setInputValue(displayName);
  }, [displayName]);

  // Filter the groups case-insensitively by substring match on the typed text.
  // While no search is active (empty input, or the input still reflects the
  // current selection), all groups stay visible.
  const filteredCourseOptions = useMemo(() => {
    const query = inputValue.trim().toLowerCase();
    if (!query || query === displayName.toLowerCase()) {
      return courseOptions;
    }
    return courseOptions.filter((group) =>
      (group.displayName ?? '').toLowerCase().includes(query),
    );
  }, [courseOptions, inputValue, displayName]);

  const selectionChangedHandler = (
    selectedGroupId: string | null,
    selectedGroupName: string | null,
  ) => {
    update?.({
      mappingState: selectedGroupId ? MappingState.ManuallyMapped : MappingState.Unassigned,
      mappingType,
      timetableCourse,
      azureGroupId: selectedGroupId ?? null,
      azureGroupName: selectedGroupName ?? null,
      suggestedAzureGroupId: assignment.suggestedAzureGroupId,
      suggestedAzureGroupName: assignment.suggestedAzureGroupName,
    });
  };

  // The reset button clears the whole row: an applied assignment is removed
  // (on save) and a pending suggestion is dismissed. The suggested
  // fields have to be cleared too, otherwise the suggestion would immediately
  // re-populate the combobox and the row would look unchanged. Nothing to do
  // when the row is already empty (no applied group and no suggestion).
  const handleResetButtonClick = () => {
    if (!appliedId && !isSuggestion) {
      return;
    }

    update?.({
      mappingState: MappingState.Unassigned,
      mappingType,
      timetableCourse,
      azureGroupId: null,
      azureGroupName: null,
      suggestedAzureGroupId: null,
      suggestedAzureGroupName: null,
    });
  };

  const errorSection = (() => {
    switch (error) {
      case ErrorType.cloudCourseError:
        return (
          <Text color="danger">
            {t('course_not_found_choose_manually', {
              defaultValue:
                'Diese Gruppe wurde nicht gefunden. Bitte die Gruppe per Dropdown auswählen.',
            })}
          </Text>
        );
      case ErrorType.duplicateAzureGroup:
        return (
          <Text color="danger">
            {t('settings:courseimport:group_assigned_twice', {
              defaultValue:
                'Diese Gruppe ist bereits einer anderen Zuordnung zugewiesen. Bitte eine der beiden Zuweisungen entfernen.',
            })}
          </Text>
        );
      default:
        return null;
    }
  })();

  // The suggestion is a proposal only. It has to be confirmed before it counts as
  // an assignment, because the backend derives suggestions from a name match and
  // two entities can share a name (Bug #11517).
  const canConfirmSuggestion = isSuggestion && !appliedId && !!assignment.suggestedAzureGroupId;

  const handleConfirmSuggestionClick = () => {
    if (!canConfirmSuggestion) {
      return;
    }

    selectionChangedHandler(
      assignment.suggestedAzureGroupId ?? null,
      assignment.suggestedAzureGroupName ?? null,
    );
  };

  const suggestionHint = isSuggestion ? (
    <Text color="muted">
      {t('settings:courseimport:suggestion_confirm_hint', {
        defaultValue:
          'Vorschlag – über das Häkchen übernehmen, zum Verwerfen „×“ verwenden.',
      })}
    </Text>
  ) : null;

  const courseName = `${t(`settings:courseimport:${timetableCourse.groupType}`)}: ${
    timetableCourse.lessonName
  }`;

  return (
    <Flex align="center" gap="8px" className="courseSetComponent">
      <Flex align="center" gap="8px" className="courseSetHeader">
        <PeopleFilled fontSize={24} />
        <Text weight="bold" className="courseName">
          {courseName}
        </Text>
      </Flex>
      <ArrowRightRegular className="mappingArrow" />
      <Flex direction="column" className="courseSetControls">
        <Flex gap="8px" align="center">
          <Combobox
            appearance={inputAppearance}
            data-testid={timetableCourse.lessonName}
            value={inputValue}
            selectedOptions={selectedId ? [selectedId] : []}
            onOptionSelect={(_, data) => {
              const id = data.optionValue ?? null;
              const group = id ? courseOptions.find((option) => option.id === id) : undefined;
              // Store the plain display name (export/import matches on it); the
              // bracketed label is only what the user sees in the field.
              selectionChangedHandler(id, group?.displayName ?? null);
              setInputValue(group ? labelFor(group) : '');
            }}
            onChange={(event) => setInputValue(event.target.value)}
            placeholder={t('select_course', { defaultValue: 'Kurs auswählen' })}
            // Open the dropdown below by default and only fall back to above so
            // it never opens sideways (before/after) when vertical space is tight.
            positioning={{
              position: 'below',
              align: 'start',
              fallbackPositions: ['above', 'above-start', 'below-start'],
            }}
            style={{ borderColor: hasError ? 'red' : undefined }}
          >
            {filteredCourseOptions.length === 0 ? (
              <Option key="no-results" value="" text="" disabled>
                {t('no_courses_found', { defaultValue: 'Keine Kurse gefunden' })}
              </Option>
            ) : (
              filteredCourseOptions.map((group) => (
                <Option key={group.id} value={group.id} text={optionLabelFor(group)}>
                  {optionLabelFor(group)}
                </Option>
              ))
            )}
          </Combobox>
          {canConfirmSuggestion && (
            <Button
              appearance="transparent"
              aria-label={t('settings:courseimport:confirm_suggestion_a11y', {
                defaultValue: 'Vorschlag übernehmen',
              })}
              icon={<CheckmarkRegular />}
              onClick={handleConfirmSuggestionClick}
            />
          )}
          <Button
            appearance="transparent"
            aria-label={t('settings:courseimport:reset_course_selection_a11y', {
              defaultValue: 'Zuweisung zurücksetzen',
            })}
            icon={<DismissRegular />}
            onClick={handleResetButtonClick}
          />
          {getIconFromMappingState({ state: assignment.mappingState, t })}
        </Flex>
        {suggestionHint}
        {errorSection}
      </Flex>
    </Flex>
  );
};
